import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads } from "../api/admin-api.js";
import { utc } from "../format.js";
import { Blank, Button, ErrorNote, Panel, Pill, ScreenHeader, ShortId, Spinner, Table, Td, Th, inputClass } from "../ui.js";

/**
 * Node keys across every account: `GET /v1/admin/node-keys` (viewer).
 *
 * NODE KEYS ARE CREDENTIALS. The key itself is never stored (only its SHA-256), so nothing here
 * can show it; a row is identified by its row id in masked form. READ ONLY: no control revokes
 * or issues a key. The endpoint has no revocation timestamp, so none is shown.
 */

const PAGE_LIMIT = 50;

export function NodeKeysScreen() {
  const [accountId, setAccountId] = useState("");
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];

  const list = useResource(
    (signal) => {
      void signal;
      return adminReads.nodeKeys({
        ...(accountId === "" ? {} : { accountId }),
        ...(cursor === undefined ? {} : { cursor }),
        limit: PAGE_LIMIT,
      });
    },
    [accountId, cursor],
  );
  const rows = list.data?.data ?? [];
  const next = list.data?.next_cursor ?? null;

  return (
    <>
      <ScreenHeader
        title="Node keys"
        lead="Every account's node keys, newest first. The key is never stored; the id is masked. Read only."
      />

      <Panel>
        <div class="grid gap-2 sm:grid-cols-3">
          <input
            class={inputClass}
            aria-label="Account id"
            placeholder="account id"
            value={accountId}
            onInput={(event) => {
              setAccountId((event.currentTarget as HTMLInputElement).value.trim());
              setCursors([]);
            }}
          />
        </div>
      </Panel>

      <div class="mt-3">
        <Panel
          title={`Node keys (${rows.length}${next === null ? "" : "+"})`}
          subtitle={`Newest first, ${PAGE_LIMIT} per page. Times are UTC, like every other screen.`}
        >
          {list.loading ? (
            <Spinner />
          ) : list.error !== null ? (
            <ErrorNote error={list.error} onRetry={() => list.reload()} />
          ) : (
            <Table
              rows={rows.length}
              empty="No node key matches. Clear the account id to list every account's keys."
              head={
                <tr>
                  <Th>Key (masked id)</Th>
                  <Th>Label</Th>
                  <Th>Account</Th>
                  <Th>Created</Th>
                  <Th>Last used</Th>
                  <Th>Status</Th>
                </tr>
              }
            >
              {rows.map((row) => (
                <tr key={`${row.key_masked}-${row.created_at}`}>
                  <Td mono>{row.key_masked}</Td>
                  <Td>{row.label ?? <Blank />}</Td>
                  <Td mono>
                    <ShortId value={row.account_id} />
                  </Td>
                  <Td mono>{utc(row.created_at)}</Td>
                  <Td mono>{row.last_used_at === null ? <Blank title="Never used" /> : utc(row.last_used_at)}</Td>
                  <Td>
                    <Pill value={row.status} tone={row.status === "active" ? "good" : "neutral"} />
                  </Td>
                </tr>
              ))}
            </Table>
          )}
          <div class="mt-3 flex gap-2">
            {cursors.length === 0 ? null : (
              <Button tone="ghost" onClick={() => setCursors([])}>
                Newest page
              </Button>
            )}
            {next === null ? null : (
              <Button tone="ghost" onClick={() => setCursors([...cursors, next])}>
                Older
              </Button>
            )}
          </div>
        </Panel>
      </div>
    </>
  );
}
